'use client';
import { CloseIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React from 'react';
import { Button } from '../../Buttons/Button/Button.mjs';
import '../../Buttons/Hotspot/Hotspot.mjs';
import '../../Buttons/Link/Link.mjs';
import '../../Buttons/NextButton/NextButton.mjs';
import '../../Buttons/InputAddonButton/InputAddonButton.mjs';
import '../../Buttons/ActionIndicator/ActionIndicator.mjs';
import '../../Buttons/ImageAction/ImageAction.mjs';
import { DsmColor } from '../../constants/colors.mjs';
import { factory } from '../../factory/factory.utils.mjs';
import { Typography } from '../../Foundations/Typography/Typography.mjs';
import { useId } from '../../Inputs/Dropdown/subcomponent/Combobox/use-id.mjs';
import { Flex } from '../../Layout/Flex/Flex.mjs';
import { Box } from '../../Other/Box/Box.mjs';
import { omitText } from '../../utils/helpers.mjs';
import { useUncontrolled } from '../../utils/hooks/use-uncontrolled.mjs';
import { Popover } from '../subcomponents/Popover/Popover.mjs';
import classes from './Coachmark.module.css.mjs';
import { useCoachmarkExternalTarget } from './Coachmark.utils.mjs';

const Coachmark = factory((props, ref) => {
  const {
    variant = "default",
    target,
    dropdown,
    position,
    size = "big",
    withArrow = true,
    className,
    targetCTA,
    closeCTA,
    opened,
    defaultOpened = false,
    heading,
    CTA,
    onChange,
    disabled,
    width,
    targetElementId,
    targetSelector,
    toggleOnExternalClick = true,
    ...rest
  } = props;
  const [dropdownOpened, setDropdownOpened] = useUncontrolled({
    value: opened,
    defaultValue: defaultOpened,
    finalValue: false,
    onChange
  });
  const id = useId();
  const isDefault = variant === "default";
  const isBig = size === "big";
  const contentColor = isDefault ? DsmColor.ContentSecondary : DsmColor.ContentOnSurfaceInfo;
  const externalOnClickRef = React.useRef();
  React.useEffect(() => {
    const fn = targetCTA?.onClick;
    externalOnClickRef.current = fn;
  }, [targetCTA]);
  const { isActive: isDynamicTarget, anchorSpanRef } = useCoachmarkExternalTarget({
    targetSelector,
    targetElementId,
    disabled,
    toggleOnExternalClick,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onToggle: () => setDropdownOpened((o) => !o),
    onExternalClick: (e) => externalOnClickRef.current?.(e),
    wrapperClassName: clsx(classes.coachmarkDropdownTarget)
  });
  return /* @__PURE__ */ React.createElement(Flex, { ...rest, ref, className: clsx("kohler-coachmark-root", className) }, /* @__PURE__ */ React.createElement(
    Popover,
    {
      opened: dropdownOpened,
      trapFocus: false,
      position,
      withinPortal: false,
      withRoles: false,
      withArrow,
      id,
      disabled,
      onChange: setDropdownOpened,
      width: isBig ? Number(width) || 180 : "max-content"
    },
    /* @__PURE__ */ React.createElement(Popover.Target, null, !disabled ? !isDynamicTarget ? /* @__PURE__ */ React.createElement(
      Box,
      {
        component: "button",
        type: "button",
        ...targetCTA,
        textColor: "inherit",
        className: clsx("kohler-coachmark-target", targetCTA?.className),
        onClick: (e) => {
          setDropdownOpened(!dropdownOpened);
          targetCTA?.onClick?.(e);
        }
      },
      target
    ) : /* @__PURE__ */ React.createElement(
      "span",
      {
        ref: anchorSpanRef,
        className: "kohler-coachmark-portal-anchor",
        style: {
          display: "inline-block",
          inset: "0",
          lineHeight: 0,
          overflow: "hidden",
          visibility: "hidden",
          position: "absolute"
        },
        "aria-hidden": "true"
      }
    ) : /* @__PURE__ */ React.createElement(Flex, { component: "div" }, target)),
    dropdown && /* @__PURE__ */ React.createElement(
      Popover.Dropdown,
      {
        onMouseDown: (event) => event.preventDefault(),
        className: clsx("kohler-coachmark-wrapper"),
        backgroundColor: isDefault ? DsmColor.SurfaceTransparent : DsmColor.SurfaceInfo
      },
      /* @__PURE__ */ React.createElement(Box, { component: "div", className: classes.dropdownWrapper }, /* @__PURE__ */ React.createElement(Flex, { direction: "column", gap: 10, paddingRight: 5 }, heading && isBig && /* @__PURE__ */ React.createElement(Typography, { ...omitText(heading), size: "label-medium-m", textColor: contentColor }, heading.text), /* @__PURE__ */ React.createElement(Typography, { component: "div", size: "paragraph-small", textColor: contentColor }, dropdown)), /* @__PURE__ */ React.createElement(
        Box,
        {
          component: "button",
          type: "button",
          "aria-label": "close popover",
          ...closeCTA,
          onClick: (e) => {
            setDropdownOpened(false);
            closeCTA?.onClick?.(e);
          },
          textColor: contentColor
        },
        /* @__PURE__ */ React.createElement(CloseIcon, { size: "small", withBoundingBox: true })
      ), CTA && isBig && /* @__PURE__ */ React.createElement(Button, { style: { gridColumnStart: "span 2" }, size: "small", fullWidth: true, variant: "secondary", ...omitText(CTA) }, CTA.text))
    )
  ));
});

export { Coachmark };
